import { useMutation, useQueryClient, QueryClient } from '@tanstack/react-query'
import { RequestTypes } from '@core/react-query/features/tasks/task'
import { RequestErrorType } from '@core/utils/api-axios-instance'
import { taskKeys } from '../taskKeys'
import { ITask } from '@core/entities/task/Task'
import { useMutateQueryData } from '@core/react-query/hooks'
import { tasksKeys } from '@core/react-query/features/tasks/tasks'
import { observationKeys } from '@core/react-query/features/observation/observationKeys'

export const getCreateTaskMutationOptions = (queryClient: QueryClient) => {
  const { deleteInfiniteQueryItem, updateInfiniteQueryItem } =
    useMutateQueryData(queryClient)
  return {
    ...taskKeys.createTask,
    onSuccess: async (task, variables) => {
      if (variables.observationId) {
        queryClient.invalidateQueries({
          queryKey: observationKeys.byId({
            observationId: variables.observationId,
          }).queryKey,
        })
      }

      // 427 idempotent replay: the task was created by an earlier attempt and
      // the response carries no body, so refetch the lists instead of patching.
      if (!task?.id) {
        if (variables.draftId) {
          queryClient.invalidateQueries({ queryKey: tasksKeys.draftTasks._def })
        }
        queryClient.invalidateQueries({ queryKey: tasksKeys.myTasks._def })
        queryClient.invalidateQueries({ queryKey: tasksKeys.allTasks._def })
        return
      }

      queryClient.setQueryData(
        taskKeys.byId({ taskId: task.id }).queryKey,
        task,
      )

      if (variables.draftId) {
        deleteInfiniteQueryItem(
          tasksKeys.draftTasks({
            organisationId: variables.organisationId,
            projectIds: [variables.projectId],
          }).queryKey,
          variables.draftId,
        )
      }

      updateInfiniteQueryItem(tasksKeys.myTasks._def, task)
      updateInfiniteQueryItem(tasksKeys.allTasks._def, task)
    },
  }
}

export const useCreateTask = () => {
  const queryClient = useQueryClient()

  return useMutation<ITask, RequestErrorType, RequestTypes['createTask']>(
    getCreateTaskMutationOptions(queryClient),
  )
}
